import React, { useEffect, useState, useRef } from 'react';
import { useIntl, useSelector, getLocale } from 'umi';
import Filter, { IMgrMoreFilterInstance } from '../../components/MgrMoreFilter';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import StoreRankMoreCore from '../../../components/StoreRankMoreCore';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useInitialTopChartsName from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialTopChartsName';
import useInitialCheckAllCountries from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialCheckAllCountries';
import useBuriedPointLogCfgKeys from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useBuriedPointLogCfgKeys';
import useStoreRankMoreCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useStoreRankMoreCore';
import useMobileStoreRankRegionOptions from '@intelligence/v3/_hooks/useMobileStoreRankRegionOptions';
import { MOBILE_STORE_RANK_COUNTRY_INHERIT_CHANGED } from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/AllTopChartsFilter/_config';
import { getAllCountries } from '@/pages/intelligence/utils/regionSelectUtils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import moment from 'moment';
import {
  PRIMARY_KEY,
  UNIFIED_KEY,
  MOBILE_SOURCES,
  MOBILE_SECTIONAL_RANK_KEYS,
  MOBILE_SECTIONAL_RANK_INFOS,
  MOBILE_SOURCES_METRICS_KEYS,
  MOBILE_PLATFORM_TOP_INFOS,
  MOBILE_SOURCE_APPSTORE_TOP_FREE,
} from '@/pages/intelligence/_config/storeRank/config';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import {
  isSameDate,
  handleGenreFilterParams,
  FilterTimeType,
} from '@intelligence/utils/filterUtils';
import {
  getFilterName,
  getFilterValueStorage,
} from '@/pages/intelligence/components/Filter/util';
import { generateGenreSelectValueSimple } from '@intelligence/v3/utils/genre';
import { AppMagicGenreTagType } from '@/pages/intelligence/components/TableComponents/components/AppMagicGenreTagList';
import { useQueryDefinition } from '@/pages/intelligence/hooks/useQueryDefinition';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IMobileStoreRankMoreFilter,
  IStoreRankMoreCoreFetchParams,
} from '@/pages/intelligence/types/storeRank';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';

const ENTITY_TYPE = 'mobile';
const SEARCH_TYPE = 'mobile-store-rank-more';
const TOP_CHARTS_TYPE = 'store-rank-more';
const BUSINESS_TYPE = `${ENTITY_TYPE}-${TOP_CHARTS_TYPE}`;
const DEFAULT_FETCH_PARAMS: IStoreRankMoreCoreFetchParams = {
  entity_type: ENTITY_TYPE,
  topchart_name: MOBILE_SOURCE_APPSTORE_TOP_FREE,
  region_type: 'market',
  regions: ['us'],
  genre: [],
  sub_genre: [],
  genre_source: 'sensortower',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
  start_release_time: '',
  end_release_time: '',
  order_source: '',
  order_metric: 'rank',
  order: 'desc',
  ratio_type: 0,
  page: 1,
  page_size: 100,
  search_ids: [],
  search_by_name: '',
};

type IMobileStoreRankMoreProps = IBaseProps;
const getEarliesTimeType = (name: string) => {
  if (name === 'appstore top grossing') {
    return hasDataTimerParams?.storerankMobileDetailTopGrossing?.type;
  }
  if (name === 'appstore top paid') {
    return hasDataTimerParams?.storerankMobileDetailTopGrossing?.type;
  }
  // 'appstore top free'
  return hasDataTimerParams?.storerankMobileDetailTopFree?.type;
};

const MobileStoreRankMore: React.FC<IMobileStoreRankMoreProps> = (props) => {
  const { } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;

  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 在筛选器这里拿到缓存的值，并渲染
  const filterName = getFilterName(userInfo);
  const filterData = getFilterValueStorage(userInfo);

  const filterState =
    useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]) ?? {};
  const isOperate = !!filterState?.isOperate;

  const { filterExtend } = useFilterExtend({});

  // 筛选器 ref，用于点击品类标签时设置筛选器值并触发请求
  const filterRef = useRef<IMgrMoreFilterInstance | null>(null);

  // 获取品类选项数据（用于点击品类标签时构建筛选器值）
  const { genreOptionsMap } = useQueryDefinition();

  // 是否初始化时勾选全部国家（从商店榜单页 点击下拉国家筛选器自定义footer按钮跳转过来的时候，固定勾选全部国家）
  const initialCheckAllCountries = useInitialCheckAllCountries();
  const initialTopChartsName = useInitialTopChartsName(MOBILE_SOURCES);
  const [initialFetchParams, setInitialFetchParams] = useState({
    ...DEFAULT_FETCH_PARAMS,
    topchart_name: initialTopChartsName,
  });

  const { pageLogCfg, downloadExcelLogCfgKey } = useBuriedPointLogCfgKeys(
    ENTITY_TYPE,
    initialTopChartsName,
  );

  const { isLoading, regionOptions: regionsOptions } = useMobileStoreRankRegionOptions();

  const { filterLoading: filterTimeLoading, latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: getEarliesTimeType(initialTopChartsName),
    rangeTimeType: FilterTimeType.sameDay,
  });

  const {
    fetchParams,
    isSectional,

    handleInit,
    handleConfirm,
    handleReset,

    isVisualisLoading,
    isVisualisNoData,
    seriesData,
    xAxisData,
    sortKeyName,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,

    isTableLoading,
    isTableNoData,
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    transTableDataForSearch,
    getTableSearchList,
    handleClearSearch,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onPageInit,
  } = useStoreRankMoreCore({
    entityType: ENTITY_TYPE,
    pageLogCfg,
    primaryKey: PRIMARY_KEY,
    platformTopMetrics: MOBILE_SOURCES_METRICS_KEYS,
    initialFetchParams,
    /**
     * 这个方法 主要是 根据fetchParams?.regions?.length > 1来判断是否为 分段式ranks
     * 其他条件 纯粹是为了加载动画的一致性（加载动画前后 模块结构保持一致，避免加载前两个卡片，加载后 突然变一个卡片 这种卡片数量变化导致页面抖动的影响），可以不用管
     * @param fetchParams 接口入参
     * @returns 是否为1-3、4-11 这种分段式ranks
     */
    getIsSectional: (fetchParams: IStoreRankMoreCoreFetchParams) => {
      if (!isOperate) return isLoading || (fetchParams?.regions?.length ?? 0) > 1;
      return (fetchParams?.regions?.length ?? 0) > 1;
    },
  });

  useEffect(() => {
    if (isLoading || !userInfo?.userid || filterTimeLoading) return;
    const countriesTmp = getAllCountries(regionsOptions);
    const multipleAllMarkets =
      countriesTmp?.length > 0 ? countriesTmp : DEFAULT_FETCH_PARAMS.regions;
    // 第一次进来，即没有继承还不用筛选器保存的情况，则默认为多国家全选，参数需要拿到国家全量值请求参数
    let newFetchParams = { ...fetchParams, regions: multipleAllMarkets };
    if (filterName) {
      const filterSaveParams = getFilterSaveParams();
      newFetchParams = {
        ...newFetchParams,
        ...filterSaveParams,
        topchart_name: initialTopChartsName,
      };
    } else if (filterState.isOperate === true) {
      /** 获取继承参数 */
      const extendParams = getExtendParams();
      newFetchParams = {
        ...newFetchParams,
        ...extendParams,
        topchart_name: initialTopChartsName,
      };
    }
    /** 若没有保存参数，也没有继承参数，则时间获取接口最后有数据的时间 */
    newFetchParams = {
      ...newFetchParams,
      start_date: newFetchParams?.start_date || moment(latestTimeTypeHasData?.date?.[0])?.format('YYYY-MM-DD'),
      end_date: newFetchParams?.end_date || moment(latestTimeTypeHasData?.date?.[1]).format('YYYY-MM-DD'),
    };
    if (initialCheckAllCountries) {
      newFetchParams = { ...newFetchParams, regions: multipleAllMarkets };
    }
    setInitialFetchParams(newFetchParams);
    onPageInit(newFetchParams);
  }, [userInfo?.userid, isLoading, regionsOptions, filterTimeLoading, latestTimeTypeHasData]);

  const getExtendParams = () => {
    return handleFilterParams(filterExtend);
  };

  const getFilterSaveParams = () => {
    return handleFilterParams(filterData?.apiValue);
  };

  const handleFilterParams = (payload: any) => {
    const params = handleRangeDate2SingleDateRangeTimeFilterExtend(payload);
    const newFetchParams: any = {};

    const timeExtend = params?.[FILTER_KEYS?.RANGE_TIME];
    const composedCountryExtend =
      params?.[FILTER_KEYS?.COMPOSED_COUNTRY_SELECT];
    const hasCountryChanged = !!params?.[MOBILE_STORE_RANK_COUNTRY_INHERIT_CHANGED]?.value;
    const genreExtend = params?.[FILTER_KEYS?.GENRETOR];
    if (timeExtend && timeExtend?.filterType === 'date') {
      const isSameDay = isSameDate(
        timeExtend?.start_date,
        timeExtend?.end_date,
        'day',
      );
      if (isSameDay) {
        // newFetchParams.granularity = timeExtend?.granularity || DEFAULT_FETCH_PARAMS.granularity;
        newFetchParams.start_date =
          timeExtend?.start_date || DEFAULT_FETCH_PARAMS.start_date;
        newFetchParams.end_date =
          timeExtend?.end_date || DEFAULT_FETCH_PARAMS.end_date;
      }
    }
    if (genreExtend) {
      Object.assign(newFetchParams, handleGenreFilterParams(genreExtend));
    }
    if (hasCountryChanged && composedCountryExtend?.value?.countries?.length > 0) {
      newFetchParams.regions = composedCountryExtend.value.countries;
    }

    // 处理 Product Model 继承
    const productModelExtend = params?.[FILTER_KEYS?.SENSOR_TOWER_TAG_PRODUCT_MODEL];
    if (productModelExtend?.value?.length > 0) {
      newFetchParams[FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL] = productModelExtend;
    }

    // 默认不继承 regions，只有 All Top Charts 手动改过国家时才在上方写入 regions
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { regions: _unusedRegions, ...defaultParamsWithoutRegions } = DEFAULT_FETCH_PARAMS;
    return {
      ...defaultParamsWithoutRegions,
      topchart_name: initialTopChartsName,
      ...newFetchParams,
    };
  };

  const handleConfirmInPage = (payload: IMobileStoreRankMoreFilter) => {
    handleConfirm(payload);
    updateURLSearchParams({ source: payload?.topchart_name });
  };

  /**
   * App Magic 品类标签点击回调
   * 点击表格中的品类标签时，更新筛选器并触发请求
   * @param type 点击的品类类型：SuperGenre(一级品类) | MajorGenre(二级品类) | MidGenre(三级品类) | SubGenre(四级品类)
   * @param superTag 一级品类值
   * @param majorTag 二级品类值（可选）
   * @param midTag 三级品类值（可选）
   * @param subTag 四级品类值（可选）
   */
  const handleAppMagicTagClick = (
    _type: AppMagicGenreTagType,
    superTag: string,
    majorTag?: string,
    midTag?: string,
    subTag?: string,
  ) => {
    // 如果品类数据不存在，则不处理
    if (!genreOptionsMap) {
      return;
    }

    // 构建新的品类筛选器值
    const newGenreValue = generateGenreSelectValueSimple(
      genreOptionsMap,
      'appmagic',
      superTag,
      majorTag,
      midTag,
      subTag,
    );
    if (!newGenreValue) {
      return;
    }

    // 通过 ref 调用筛选器的 setValueAndConfirm 方法
    // 这样可以确保筛选器 UI 状态和 API 请求参数都正确更新
    filterRef.current?.setValueAndConfirm({
      [FILTER_KEYS.GENRETOR]: newGenreValue,
    });
  };

  return (
    <>
      <MarqueeBanner />
      <StoreRankMoreCore
        entityType={ENTITY_TYPE}
      searchType={SEARCH_TYPE}
      pageLogInfo={pageLogCfg}
      title={formatMessage({ id: 'dbi_store_rank_mobile_top_more_title' })}
      isOperate={isOperate}
      isSectional={isSectional}
      isShowVisualis={!isSectional}
      fetchParams={fetchParams}
      visualisOptions={{
        isVisualisLoading,
        isVisualisNoData,
        seriesData,
        xAxisData,
        sortKeyName,
        currentLine,
        segments,
        segment,
        setSegment,
        yAxisFormatter,
        tooltipFormatter,
        createLegendsCustomMade,
      }}
      tablesOptions={{
        isTableLoading,
        isTableNoData,
        // 表格数据的主键，表格的recordKey
        primaryKey: PRIMARY_KEY,
        uunifiedKey: UNIFIED_KEY,
        // 分段排名rank模式时，表格数据的主键，可视化需要用到（获取图片），mobile 分段排名rank模式 暂时没有可视化，所以这个字段在mobile没用
        imageKey: 'cover',
        sectionalRankKeys: MOBILE_SECTIONAL_RANK_KEYS,
        sectionalRankInfos: MOBILE_SECTIONAL_RANK_INFOS,
        platformTopMetrics: MOBILE_SOURCES_METRICS_KEYS,
        platformTopInfos: MOBILE_PLATFORM_TOP_INFOS,
        tableDimensionsData,
        tableTotal,
        tableData,
        tableSelection,
        defaultSelectionGames,
        transTableDataForSearch,
        getTableSearchList,
        handleClearSearch,
        onTableSortChage,
        onTablePagingChange,
        onTableCheckGame,
        genreOptionsMap,
        onAppMagicTagClick: handleAppMagicTagClick,
      }}
      filter={
        <Filter
          ref={filterRef}
          isOperate={isOperate}
          buttonLogConfig={{}}
          initialCheckAllCountries={initialCheckAllCountries}
          regionOptions={regionsOptions}
          filter={fetchParams}
          onInit={handleInit}
          onConfirm={handleConfirmInPage}
          onReset={handleReset}
          rangeTimeComponentDefault={latestTimeTypeHasData}
          entityType={ENTITY_TYPE}
          filterLoading={filterTimeLoading || isLoading}
        />
      }
      exportTable={
        <ExportTable
          entityType={ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={downloadExcelLogCfgKey}
          isDisableDownload={isTableLoading}
          isNeedCheckedLines={!isSectional}
          // downloadModalSize={isCN || isSectional ? 500 : 600}
          downloadModalSize={600}
          tableParams={{
            ...fetchParams,
            downlaod_date_method: 'aggregated',
            page_size: 5000,
          }}
          total={tableTotal}
          getCheckedLinesCfg={() => ({
            key: 'search_ids',
            value: isSectional
              ? []
              : tableSelection?.map((item) => item?.app_id) || [],
          })}
          useWebDownload={true}
          useDownloadAmountType={true}
          downloadKey={'mobileStoreRankMore'}
        />
      }
    />
    </>
  );
};

export default MobileStoreRankMore;
